// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, database } from '../firebase/Config';
import { ref, get, child, onValue, set } from 'firebase/database';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userIsActive, setUserIsActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [userFullData, setUserFullData] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setCurrentUser(user);
        // Buscar dados do usuário no Realtime Database
        await fetchUserRole(user.uid);
      } else {
        setCurrentUser(null);
        setUserRole(null);
        setUserIsActive(true);
        setUserFullData(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchUserRole = async (uid) => {
    try {
      const dbRef = ref(database);
      const snapshot = await get(child(dbRef, `users/${uid}`));
      
      if (snapshot.exists()) {
        const userData = snapshot.val();
        setUserRole(userData.role || 'user'); // Default: user
        setUserIsActive(userData.isActive !== undefined ? userData.isActive : true);
        setUserFullData(userData);

        // Listener em tempo real para mudanças no role
        const userRef = ref(database, `users/${uid}`);
        onValue(userRef, (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.val();
            setUserRole(data.role || 'user');
            setUserIsActive(data.isActive !== undefined ? data.isActive : true);
            setUserFullData(data);
          }
        });
      } else {
        console.warn('Usuário não encontrado no database. Criando registro básico...');
        
        // Criar registro básico para usuário que existe no Auth mas não no Database
        const currentUser = auth.currentUser;
        if (currentUser) {
          const newUserData = {
            uid: uid,
            email: currentUser.email,
            firstName: currentUser.displayName || currentUser.email.split('@')[0],
            lastName: '',
            phone: '',
            company: '',
            photoURL: currentUser.photoURL || '',
            role: 'user', // Default para novos usuários
            isActive: true,
            emailVerified: currentUser.emailVerified || false,
            createdAt: new Date().toISOString(),
          };

          // Salvar no database
          const userRef = ref(database, `users/${uid}`);
          await set(userRef, newUserData);
          
          console.log('Registro de usuário criado automaticamente');
          setUserRole('user');
          setUserIsActive(true);
          setUserFullData(newUserData);
        } else {
          // Fallback se não conseguir criar
          setUserRole('user');
          setUserIsActive(true);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar role do usuário:', error);
      setUserRole('user'); // Default em caso de erro
      setUserIsActive(true);
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = () => {
    return userRole === 'admin';
  };

  const isUser = () => {
    return userRole === 'user';
  };

  const isActive = () => {
    return userIsActive;
  };

  const hasPermission = (requiredRole) => {
    if (requiredRole === 'admin') {
      return userRole === 'admin';
    }
    if (requiredRole === 'user') {
      return userRole === 'user' || userRole === 'admin';
    }
    return false;
  };

  const value = {
    currentUser,
    userRole,
    userIsActive,
    userFullData,
    loading,
    isAdmin,
    isUser,
    isActive,
    hasPermission,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
